// T10: Mike chose the cleared count to open seven days of his decisions, with Undo (6 October).

import { ActionButton } from '../parts/ActionButton.js';
import { useEffect, useState } from 'react';
import { Unauthenticated, type Store } from '../api.js';
import { readCleared, undoCleared, type ClearedRow } from './api.js';
import { useLiveRefresh, LIVE_BEAT_MS } from '../live.js';
import './cleared.css';

export function ClearedHistory({ stores, revision, onUndone }: { stores: Store[]; revision: number; onUndone: () => void }) {
  const [rows, setRows] = useState<ClearedRow[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const key = stores.map((store) => store.tenantId).join('+');
  useEffect(() => {
    const control = new AbortController();
    setProblem(null);
    readCleared(stores, control.signal).then((fresh) => {
      if (!control.signal.aborted) setRows(fresh);
    }).catch((error: unknown) => {
      if (control.signal.aborted) return;
      if (error instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setProblem(error instanceof Error ? error.message : String(error));
    });
    return () => control.abort();
    // The enclosing component is keyed by stores; a new array for the same stores is not a new read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick, revision]);
  useLiveRefresh(() => setTick((value) => value + 1), LIVE_BEAT_MS);

  const undo = async (row: ClearedRow) => {
    if (busy) return;
    setBusy(`${row.store.tenantId}:${row.decisionId}`); setProblem(null);
    try {
      await undoCleared(row);
      setRows((current) => current?.filter((item) => item.store.tenantId !== row.store.tenantId || item.decisionId !== row.decisionId) ?? null);
      onUndone();
    } catch (error) {
      if (error instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setProblem(error instanceof Error ? error.message : String(error));
    } finally { setBusy(null); }
  };

  return <section className="cleared-history" aria-labelledby="cleared-history-title" id="cleared-history">
    <h2 id="cleared-history-title">Cleared in the last seven days</h2>
    {problem ? <><p role="alert">{problem}</p>{rows === null ? <ActionButton reading type="button" className="link inline" onClick={() => setTick((value) => value + 1)}>Try again</ActionButton> : null}</> : null}
    {rows === null && !problem ? <p role="status">Reading the cleared cards…</p> : null}
    {rows?.length === 0 ? <p>No cards cleared in the last seven days.</p> : null}
    {rows && rows.length > 0 ? <ol>{rows.map((row) => <li key={`${row.store.tenantId}:${row.decisionId}`}>
      <div><strong>{row.headline ?? row.subjectKind.replace(/_/g, ' ')}</strong><p className="meta">{row.store.name} · {row.optionLabel ?? row.action} · <time dateTime={row.decidedAt}>{new Date(row.decidedAt).toLocaleString()}</time></p></div>
      {row.undo === 'undo' && Date.parse(row.undoUntil) > Date.now()
        ? <ActionButton type="button" className="link inline" disabled={busy !== null} onClick={() => void undo(row)} aria-label={`Undo: ${row.headline ?? row.action}`}>{busy === `${row.store.tenantId}:${row.decisionId}` ? 'Undoing…' : 'Undo'}</ActionButton>
        : <span className="meta">{row.undo === 'undo' ? 'That was more than seven days ago, so it can no longer be undone.' : 'That has gone out, so it cannot be undone.'}</span>}
    </li>)}</ol> : null}
  </section>;
}
